import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, pageImageUrl, paperPdfUrl, sharedPageImageUrl, sharedPdfUrl, siteLink } from './api';
import { ClipDialog } from './ClipDialog';
import { CropLayer } from './CropLayer';
import { Dock, useNarrow } from './Dock';
import { Flip, getTurnStyle, isTurnStyle, PageFlip, setTurnStyle, TURN_LABEL, TurnMenu, TurnStyle } from './PageFlip';
import { SoundMenu } from './SoundMenu';
import { loadShareExtras } from './extras';
import { clipWithLogo, plainPicture } from './logoBand';
import { shortDate } from './dates';
import { IconDownload, IconGrid, IconLeft, IconClose, IconPlus, IconRight, IconScissors, IconShare } from './Icons';
import { ShareDialog } from './ShareDialog';
import { getSoundMode, isSoundId, playTurn, setSoundMode, setSoundOverride, SoundId } from './sound';
import { useToast } from './Toast';

export type Source = { kind: 'auth'; date: string } | { kind: 'share'; token: string; date: string };

interface Props {
  source: Source;
  pageCount: number;
  page: number;
  onPage: (p: number) => void;
  after?: React.ReactNode; // extra button at the right end of the tool bar (Archive)
  nav?: React.ReactNode; // pagination, Edition and Archives, shown at the left of the tool bar
  top?: React.ReactNode; // shown above the sheet (the advertisement on page 1)
  onFix?: () => void; // when a page cannot be loaded: open the upload / delete dialog for this day
  /** Opened from a shared link: the page effect and sound chosen by the person who shared it (the reader cannot change them). */
  locked?: { effect?: string; sound?: string } | null;
}

export function Reader({ source, pageCount, page, onPage, nav, after, top, onFix, locked }: Props) {
  const toast = useToast();
  const narrow = useNarrow();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(0);
  const [reason, setReason] = useState('');
  const [full, setFull] = useState(false);
  const [crop, setCrop] = useState(false);
  const [clip, setClip] = useState<Blob | null>(null);
  const [thumbs, setThumbs] = useState(false);
  const [share, setShare] = useState<{ url: string; page: number; blob: Blob | null; token?: string } | null>(null);
  // A shared link shows only the effect and the sound chosen by the person who shared it.
  const isLocked = source.kind === 'share';
  const lockedTurn: TurnStyle = isTurnStyle(locked?.effect) ? (locked?.effect as TurnStyle) : 'none';
  const lockedSound: SoundId = isSoundId(locked?.sound) ? (locked?.sound as SoundId) : 'off';
  const [userSound, setUserSound] = useState<SoundId>(() => {
    setSoundOverride(null);
    return getSoundMode();
  });
  const sound: SoundId = isLocked ? lockedSound : userSound;
  const [fsZoom, setFsZoom] = useState(1);
  const [fsBase, setFsBase] = useState(1); // the zoom that fits the screen (the "100%" of the buttons)
  // full screen: until the person zooms by hand, the page is scaled up just enough to cover the whole screen (so no black block is left under it)
  const fsStageRef = useRef<HTMLDivElement>(null);
  const fsImgRef = useRef<HTMLImageElement>(null);
  const fsAuto = useRef(true);
  const coverFs = useCallback(() => {
    const st = fsStageRef.current;
    const im = fsImgRef.current;
    if (!fsAuto.current || !st || !im || !im.naturalWidth || !st.clientWidth) return;
    // the zoom at which the page is exactly as high as the screen
    const need = (st.clientHeight * im.naturalWidth) / (st.clientWidth * im.naturalHeight);
    // a wide (computer) screen shows the WHOLE page, nothing cut off; a tall (phone) screen is covered completely, so no black block is left
    const base = st.clientWidth >= st.clientHeight ? Math.min(1, Math.floor(need * 100) / 100) : Math.min(4, Math.max(1, Math.ceil(need * 100) / 100));
    setFsBase(base);
    setFsZoom(base);
    window.requestAnimationFrame(() => {
      st.scrollLeft = Math.max(0, (st.scrollWidth - st.clientWidth) / 2);
      st.scrollTop = 0;
    });
  }, []);
  // the page turning like a leaf of a book
  const [flip, setFlip] = useState<Flip | null>(null);
  const [userTurn, setUserTurn] = useState<TurnStyle>(getTurnStyle());
  const turn: TurnStyle = isLocked ? lockedTurn : userTurn;
  const last = useRef({ key: '', page: 0 });
  const [hiFailed, setHiFailed] = useState(false);
  // how many real pixels wide the page is shown (window width x screen density; browser zoom changes both)
  const [vw, setVw] = useState(() => Math.round(window.innerWidth * (window.devicePixelRatio || 1)));
  useEffect(() => {
    const f = () => setVw(Math.round(window.innerWidth * (window.devicePixelRatio || 1)));
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);

  // primitives only, so a parent re-render never looks like "a different newspaper"
  const kind = source.kind;
  const date = source.date;
  const token = source.kind === 'share' ? source.token : '';
  const sourceKey = kind + ':' + date + ':' + token;

  // Zooming in, or a big / sharp screen, asks the server for the extra sharp picture (3000 px wide) so small print stays readable
  const hi = !hiFailed && vw * (full ? fsZoom : 1) > 1700;
  const pageW = hi ? 3000 : 1800;

  const urlRef = useRef<(p: number, w?: number) => string>(() => '');
  const url = useCallback(
    (p: number, w = 1800) => (kind === 'auth' ? pageImageUrl(date, p, w) : sharedPageImageUrl(token, p, w)),
    [kind, date, token]
  );

  urlRef.current = url;
  useEffect(() => {
    setSoundOverride(isLocked ? lockedSound : null);
    return () => setSoundOverride(null);
  }, [isLocked, lockedSound]);
  useEffect(() => {
    const p = last.current;
    const same = p.key === sourceKey && p.page > 0 && p.page !== page;
    const calm = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (same && !calm && turn !== 'none') setFlip({ style: turn, dir: page > p.page ? 'next' : 'prev', from: urlRef.current(p.page, pageW), to: urlRef.current(page, pageW), id: Date.now() });
    else if (!same) setFlip(null);
    last.current = { key: sourceKey, page };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sourceKey]);

  // When the picture cannot be loaded, ask the server why so the message can say what to fix.
  useEffect(() => {
    if (failed < 2) {
      setReason('');
      return;
    }
    let cancelled = false;
    const target = url(page);
    fetch(target)
      .then(async (r) => {
        if (r.ok) return 'The server sent the page, but the browser could not show it. Try again.';
        let msg = '';
        try {
          const j = await r.json();
          msg = (j && (j.error || j.message)) || '';
        } catch {
          /* not JSON */
        }
        if (r.status === 401 || r.status === 403) return 'Your sign-in has expired. Sign out and enter your access code again.';
        if (r.status === 404) return msg || 'This page does not exist on the server (' + r.status + ').';
        return (msg || 'The server could not prepare this page') + ' (error ' + r.status + ').';
      })
      .catch(() => 'The server could not be reached. Check that the backend is running (' + target.split('/api')[0] + ').')
      .then((m) => {
        if (!cancelled) setReason(m);
      });
    return () => {
      cancelled = true;
    };
  }, [failed, page, url]);


  useEffect(() => {
    setLoaded(false);
    setFailed(0);
    setCrop(false);
    stageRef.current?.scrollTo({ top: 0, left: 0 });
  }, [page, sourceKey]);

  // an image that finished loading before React attached its handler (cache hit) would stay "loading" forever
  useEffect(() => {
    const i = imgRef.current;
    if (i && i.complete && i.naturalWidth > 0) setLoaded(true);
  }, [page, failed, sourceKey]);

  // warm up the neighbouring pages so turning is instant
  useEffect(() => {
    [page + 1, page - 1].forEach((p) => {
      if (p >= 1 && p <= pageCount) {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.src = url(p);
      }
    });
  }, [page, pageCount, url]);

  const go = useCallback(
    (delta: number) => {
      const n = page + delta;
      if (n < 1 || n > pageCount) return;
      playTurn(delta > 0 ? 'next' : 'prev', n);
      onPage(n);
    },
    [page, pageCount, onPage]
  );

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') go(1);
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') go(-1);
      else if (e.key === 'Escape') setCrop(false);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [go]);

  const pickTurn = (s: TurnStyle) => {
    setUserTurn(s);
    setTurnStyle(s);
    toast(TURN_LABEL[s], 'info');
  };

  const pickSound = (s: SoundId) => {
    setUserSound(s);
    setSoundMode(s);
    if (s !== 'off') playTurn('next', page);
  };

  // the day's adverts and both flash-news bars go onto every picture that is shared or cut out (not available on a public shared link)
  const extrasToken = source.kind === 'share' ? source.token : undefined;
  const loadExtras = useCallback(() => loadShareExtras(source.date, source.kind === 'auth', extrasToken), [source.date, source.kind, extrasToken]);

  const openShare = async () => {
    // the whole page picture (with the logo on top) goes together with the link, never the link alone
    let blob: Blob | null = null;
    const img = imgRef.current;
    try {
      if (img && img.complete && img.naturalWidth > 0 && page === 1) {
        // page 1 already carries the newspaper name, so it is shared as it is, without the logo on top
        blob = await plainPicture(img, await loadExtras());
      } else if (img && img.complete && img.naturalWidth > 0) {
        blob = await clipWithLogo(img, 0, 0, img.naturalWidth, img.naturalHeight, await loadExtras());
      }
    } catch {
      blob = null;
    }
    if (source.kind === 'share') {
      // a link for exactly this page, whose chat preview is this page's picture
      let u = window.location.href;
      try {
        u = (await api.publicPageLink(source.token, page)).url;
      } catch {
        /* keep the address of the open page */
      }
      setShare({ url: u, page, blob }); // a public viewer cannot see the counts
      return;
    }
    // Direct link to this page, built from the address the site is open on, e.g. http://localhost:3000/#/p/2026-10-14/1
    // It opens this page (previous / next keep working); the reader signs in with their access code first if needed.
    // a public link whose preview in chat apps is the picture of this very page (not the app icon)
    let url = siteLink(`/p/${source.date}/${page}?share=1`);
    let token: string | undefined;
    try {
      const l = await api.share(source.date, page, turn, sound);
      url = l.url;
      token = l.token;
    } catch {
      /* keep the direct link */
    }
    setShare({ url, page, blob, token });
  };

  const openFull = () => {
    fsAuto.current = true;
    setCrop(false);
    setFsZoom(1);
    setFull(true);
    try {
      document.documentElement.requestFullscreen?.().catch(() => undefined);
    } catch {
      /* the overlay alone is fine */
    }
  };
  const closeFull = useCallback(() => {
    setFull(false);
    try {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    } catch {
      /* ignore */
    }
  }, []);
  // a new page, or the screen changing size (the browser going full screen, turning the phone), starts again from "cover the screen"
  useEffect(() => {
    if (!full) return;
    fsAuto.current = true;
    const t = window.setTimeout(coverFs, 60);
    const r = () => coverFs();
    window.addEventListener('resize', r);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', r);
    };
  }, [full, page, coverFs]);
  // leaving the browser's full screen with Esc also closes the view
  useEffect(() => {
    if (!full) return;
    const h = () => {
      if (!document.fullscreenElement) setFull(false);
    };
    document.addEventListener('fullscreenchange', h);
    const k = (e: KeyboardEvent) => e.key === 'Escape' && closeFull();
    window.addEventListener('keydown', k);
    return () => {
      document.removeEventListener('fullscreenchange', h);
      window.removeEventListener('keydown', k);
    };
  }, [full, closeFull]);

  const tools = (
      <div className="tool-group">
          {source.kind === 'share' && (
            <button className="icon-btn" onClick={() => setThumbs((t) => !t)} aria-pressed={thumbs} aria-label="All pages" title="All pages">
              <IconGrid />
            </button>
          )}
          {!isLocked && <TurnMenu value={userTurn} onChange={pickTurn} />}
          {!isLocked && <SoundMenu value={userSound} onChange={pickSound} />}
          <button className="cbtn tone-indigo" onClick={openFull} title="Show the page in full screen">
            <IconPlus size={16} /> <span>Zoom</span>
          </button>
          <button className={'cbtn tone-red scissors' + (crop ? ' on' : '')} onClick={() => setCrop((c) => !c)} aria-pressed={crop} title="Crop a story">
            <IconScissors size={18} /> <span>Crop</span>
          </button>
          <a className="cbtn tone-brown pdf-btn" href={source.kind === 'auth' ? paperPdfUrl(source.date) : sharedPdfUrl(token)} download={source.date + '.pdf'} title={'Download the paper of ' + source.date + ' as ' + source.date + '.pdf'}>
            <IconDownload size={16} /> <span>PDF</span>
          </a>
          <button className="cbtn tone-cyan" onClick={openShare} title="Share this page link">
            <IconShare size={18} /> <span>Share</span>
          </button>
          {after}
        </div>
  );

  return (
    <section className="reader" aria-label="Newspaper reader">
      {narrow ? (
        <Dock>
          {nav ? <div className="tools-nav">{nav}</div> : null}
          {tools}
        </Dock>
      ) : (
        <div className="reader-tools">
          {nav ? (
            <div className="tools-nav">{nav}</div>
          ) : (
            <div className="where">
              <strong>{shortDate(source.date)}</strong>
              <span>
                Page {page} of {pageCount}
              </span>
            </div>
          )}
          {tools}
        </div>
      )}

      <div className="stage" ref={stageRef}>
        {top}
        <div className="sheet">
          {flip && <PageFlip flip={flip} viewH={stageRef.current?.clientHeight || 700} onDone={() => setFlip(null)} />}
          {!loaded && failed < 2 && !flip && <div className="skeleton" aria-hidden="true" />}
          {failed >= 2 && (
            <div className="img-fail">
              <p>This page could not be loaded.</p>
              {reason && <p className="img-fail-why">{onFix ? reason : 'This page is not available right now. Please try again in a little while.'}</p>}
              <button className="btn" onClick={() => setFailed(0)}>
                Try again
              </button>
              {onFix && (
                <button className="btn" onClick={onFix}>
                  Delete this day and upload again…
                </button>
              )}
            </div>
          )}
          {failed < 2 && (
            <img
              key={page + ':' + failed}
              ref={imgRef}
              className={'page-img' + (loaded ? ' in' : '')}
              src={url(page, pageW)}
              crossOrigin="anonymous"
              alt={`Newspaper ${shortDate(source.date)}, page ${page}`}
              draggable={false}
              onLoad={() => setLoaded(true)}
              onError={() => (hi ? setHiFailed(true) : setFailed((f) => f + 1))}
            />
          )}
          {crop && loaded && (
            <CropLayer
              imgRef={imgRef}
              date={source.date}
              page={page}
              onCancel={() => setCrop(false)}
              onCut={(b) => {
                setCrop(false);
                setClip(b);
              }}
              onError={(m) => toast(m, 'error')}
              loadExtras={loadExtras}
            />
          )}
        </div>
      </div>

      <button className="nav-arrow prev" onClick={() => go(-1)} disabled={page <= 1} aria-label="Previous page">
        <IconLeft size={28} />
      </button>
      <button className="nav-arrow next" onClick={() => go(1)} disabled={page >= pageCount} aria-label="Next page">
        <IconRight size={28} />
      </button>

      {thumbs && source.kind === 'share' && (
        <aside className="thumbs" aria-label="All pages">
          <header>
            <strong>All pages</strong>
            <button className="icon-btn" onClick={() => setThumbs(false)} aria-label="Close">
              ×
            </button>
          </header>
          <div className="thumb-grid">
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                className={'thumb' + (p === page ? ' cur' : '')}
                onClick={() => {
                  if (p !== page) {
                    playTurn(p > page ? 'next' : 'prev', p);
                    onPage(p);
                  }
                  setThumbs(false);
                }}
              >
                <img src={url(p, 400)} loading="lazy" alt="" />
                <span>{p}</span>
              </button>
            ))}
          </div>
        </aside>
      )}

      {full && (
        <div className="fs" role="dialog" aria-modal="true" aria-label="Full screen page">
          <div className="fs-bar">
            <span>
              {shortDate(source.date)} · Page {page} of {pageCount}
            </span>
            <div className="fs-zoom" role="group" aria-label="Zoom">
              <button className="cbtn tone-indigo" onClick={() => { fsAuto.current = false; setFsZoom((z) => Math.max(Math.min(1, fsBase), +(z / 1.4).toFixed(3))); }} disabled={fsZoom <= Math.min(1, fsBase) + 0.001} aria-label="Zoom out">−</button>
              <button className="cbtn tone-slate" onClick={() => { fsAuto.current = true; coverFs(); setFsZoom(fsBase); }} title="Fit the page to the screen">{Math.round((fsZoom / fsBase) * 100)}%</button>
              <button className="cbtn tone-indigo" onClick={() => { fsAuto.current = false; setFsZoom((z) => Math.min(4, +(z * 1.4).toFixed(3))); }} disabled={fsZoom >= 4} aria-label="Zoom in">+</button>
            </div>
            <button className="cbtn tone-red" onClick={closeFull} aria-label="Close full screen">
              <IconClose size={16} /> <span>Close</span>
            </button>
          </div>
          <div className="fs-stage" ref={fsStageRef}>
            <img
              ref={fsImgRef}
              onLoad={coverFs}
              src={url(page, pageW)}
              alt={`Newspaper ${shortDate(source.date)}, page ${page}`}
              draggable={false}
              style={{ width: `${fsZoom * 100}%` }}
              onDoubleClick={() => { fsAuto.current = false; setFsZoom((z) => (z > fsBase * 1.3 ? fsBase : Math.min(4, fsBase * 2.5))); }}
              onError={() => hi && setHiFailed(true)}
            />
          </div>
          <button className="nav-arrow prev" onClick={() => go(-1)} disabled={page <= 1} aria-label="Previous page">
            <IconLeft size={28} />
          </button>
          <button className="nav-arrow next" onClick={() => go(1)} disabled={page >= pageCount} aria-label="Next page">
            <IconRight size={28} />
          </button>
        </div>
      )}
      {clip && <ClipDialog blob={clip} date={source.date} page={page} canSave={source.kind === 'auth'} onClose={() => setClip(null)} />}
      {share && (
        <ShareDialog title="Share this page" text="" url={share.url} blob={share.blob} withLink views={share.token ? { kind: 'share', token: share.token } : undefined} onClose={() => setShare(null)} />
      )}
    </section>
  );
}
